import { useState } from 'react';
import api from '../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Eye, EyeOff, Lock, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';

function scorePassword(pw) {
  let score = 0;
  if (!pw) return 0;
  if (pw.length >= 12) score += 1;
  if (pw.length >= 16) score += 1;
  if (/[A-Z]/.test(pw)) score += 1;
  if (/[a-z]/.test(pw)) score += 1;
  if (/\d/.test(pw)) score += 1;
  if (/[^A-Za-z0-9]/.test(pw)) score += 1;
  return Math.min(score, 6);
}

const STRENGTH_LABELS = ['Too weak', 'Weak', 'Fair', 'Good', 'Strong', 'Excellent', 'Excellent'];
const STRENGTH_COLORS = ['bg-rose-500', 'bg-rose-400', 'bg-amber-400', 'bg-yellow-400', 'bg-emerald-400', 'bg-emerald-500', 'bg-emerald-600'];

export default function ChangePasswordDialog({ open, onOpenChange, forced = false }) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showC, setShowC] = useState(false);
  const [showN, setShowN] = useState(false);
  const [showX, setShowX] = useState(false);
  const [busy, setBusy] = useState(false);

  const score = scorePassword(next);
  const match = next && next === confirm;
  const okLen = next.length >= 12;
  const okUpper = /[A-Z]/.test(next);
  const okLower = /[a-z]/.test(next);
  const okNum = /\d/.test(next);
  const okSpec = /[^A-Za-z0-9]/.test(next);
  const canSubmit = okLen && okUpper && okLower && okNum && okSpec && match && current.length > 0;

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    try {
      await api.post('/auth/change-password', { current_password: current, new_password: next });
      toast.success('Password updated');
      setCurrent(''); setNext(''); setConfirm('');
      onOpenChange?.(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to change password');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={forced ? () => {} : onOpenChange}>
      <DialogContent className="max-w-md" data-testid="change-password-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Lock className="w-5 h-5 text-magenta" />
            {forced ? 'Update your password' : 'Change password'}
          </DialogTitle>
        </DialogHeader>
        {forced && (
          <div className="text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded-lg p-3">
            An administrator has requested you set a new password before continuing.
          </div>
        )}
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Current password</Label>
            <div className="relative">
              <Input type={showC ? 'text' : 'password'} value={current} onChange={(e) => setCurrent(e.target.value)} data-testid="current-password" />
              <button type="button" className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400" onClick={() => setShowC((v) => !v)}>
                {showC ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div>
            <Label className="text-xs">New password</Label>
            <div className="relative">
              <Input type={showN ? 'text' : 'password'} value={next} onChange={(e) => setNext(e.target.value)} data-testid="new-password" />
              <button type="button" className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400" onClick={() => setShowN((v) => !v)}>
                {showN ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <div className="mt-2 flex gap-1" data-testid="password-strength">
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <div key={i} className={`h-1.5 flex-1 rounded ${i < score ? STRENGTH_COLORS[score - 1] : 'bg-slate-200'}`} />
              ))}
            </div>
            <div className="text-[11px] text-slate-500 mt-1">{next ? STRENGTH_LABELS[score] : 'Password strength'}</div>
            <ul className="mt-2 text-[11px] space-y-0.5">
              <li className={okLen ? 'text-emerald-600' : 'text-slate-400'}>• At least 12 characters</li>
              <li className={okUpper ? 'text-emerald-600' : 'text-slate-400'}>• One uppercase letter</li>
              <li className={okLower ? 'text-emerald-600' : 'text-slate-400'}>• One lowercase letter</li>
              <li className={okNum ? 'text-emerald-600' : 'text-slate-400'}>• One number</li>
              <li className={okSpec ? 'text-emerald-600' : 'text-slate-400'}>• One special character</li>
            </ul>
          </div>
          <div>
            <Label className="text-xs">Confirm new password</Label>
            <div className="relative">
              <Input type={showX ? 'text' : 'password'} value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="confirm-password" />
              <button type="button" className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400" onClick={() => setShowX((v) => !v)}>
                {showX ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {confirm && !match && <div className="text-[11px] text-rose-600 mt-1">Passwords do not match</div>}
            {confirm && match && <div className="text-[11px] text-emerald-600 mt-1 flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> Passwords match</div>}
          </div>
        </div>
        <DialogFooter>
          {!forced && <Button variant="outline" onClick={() => onOpenChange?.(false)}>Cancel</Button>}
          <Button onClick={submit} disabled={!canSubmit || busy} className="bg-magenta hover:bg-magenta/90" data-testid="submit-password">
            {busy ? 'Saving…' : 'Update password'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
